import type { ListBuilderConfiguration } from '../types';

/**
 * Payment Schedule Occurrences list configuration for the client-level
 * occurrences embedded list. Read-only — occurrences are system-generated.
 */
export const PAYMENT_SCHEDULE_OCCURRENCES_LIST_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-payment-schedule-occurrences',
				cacheTTL: 300000,
				name: 'Payment Schedule Occurrences',
				type: 'default',
				active: true,
				columns: [
					{
						// PD-15867 — the tenant-wide pending cycle charges page needs
						// to say WHOSE charge each row is. On the client's own
						// Upcoming sub-tab the value repeats the account being
						// viewed; the column picker hides it there.
						id: 'occurrence-client',
						headers: [
							{
								id: 'occurrence-client-header',
								text: 'Client',
							},
						],
						fields: [
							{
								id: 'occurrence-client-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'clientName',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
								link: {
									enabled: true,
									type: 'custom',
									urlTemplate: '/clients/{clientId}/payments',
								},
							},
						],
						sortable: false,
					},
					{
						id: 'occurrence-schedule-name',
						headers: [
							{
								id: 'occurrence-schedule-name-header',
								text: 'Schedule',
							},
						],
						fields: [
							{
								id: 'occurrence-schedule-name-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'scheduleName',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
							},
						],
						sortable: true,
					},
					{
						id: 'occurrence-scheduled-date',
						headers: [
							{
								id: 'occurrence-scheduled-date-header',
								text: 'Scheduled Date',
							},
						],
						fields: [
							{
								id: 'occurrence-scheduled-date-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'scheduledDate',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'date',
							},
						],
						sortable: true,
					},
					{
						id: 'occurrence-amount',
						headers: [
							{
								id: 'occurrence-amount-header',
								text: 'Amount',
							},
						],
						fields: [
							{
								id: 'occurrence-amount-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'amount',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
									formatOptions: {
										numberFormat: 'currency',
									},
								},
								displayStyle: 'number',
							},
						],
						sortable: true,
					},
					{
						id: 'occurrence-status',
						headers: [
							{
								id: 'occurrence-status-header',
								text: 'Status',
							},
						],
						fields: [
							{
								id: 'occurrence-status-field',
								dataConfig: {
									type: 'field',
									// PD-15867 — `statusDisplay`, not `status`: a projected
									// autopay row has no occurrence and renders
									// "Projected" without `projected` becoming a real
									// status on the entity.
									field: {
										fieldName: 'statusDisplay',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'status_map',
										statusMap: {
											projected: { type: 'default' },
											planned: { type: 'info' },
											pending: { type: 'info' },
											released: { type: 'warning' },
											processing: { type: 'warning' },
											retrying: { type: 'warning' },
											succeeded: { type: 'success' },
											failed: { type: 'danger' },
											skipped: { type: 'default' },
											cancelled: { type: 'default' },
										},
									},
								},
								displayStyle: 'badge',
							},
						],
						sortable: true,
					},
					{
						id: 'occurrence-invoice',
						headers: [
							{
								id: 'occurrence-invoice-header',
								text: 'Invoice',
							},
						],
						fields: [
							{
								id: 'occurrence-invoice-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'invoiceExternalId',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
							},
						],
						sortable: false,
					},
					{
						// PD-15867 — a cycle occurrence that is waiting on a person.
						// Without it the Upcoming list gives no clue that money is
						// being held up by an unmade decision.
						id: 'occurrence-requires-release',
						headers: [
							{
								id: 'occurrence-requires-release-header',
								text: 'Requires Release',
							},
						],
						fields: [
							{
								id: 'occurrence-requires-release-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'requiresRelease',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'status_map',
										statusMap: {
											true: { type: 'warning' },
											false: { type: 'default' },
										},
									},
									valueMapping: [
										{ value: true, displayText: 'Yes' },
										{ value: false, displayText: '-' },
									],
								},
								displayStyle: 'badge',
							},
						],
						sortable: false,
						alignment: 'center',
					},
					{
						// PD-15867 — why a planned charge did not collect (below the
						// tenant's minimum, nothing owed). The status alone says
						// "skipped" without saying why.
						id: 'occurrence-skip-reason',
						headers: [
							{
								id: 'occurrence-skip-reason-header',
								text: 'Skip Reason',
							},
						],
						fields: [
							{
								id: 'occurrence-skip-reason-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'skipReason',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
							},
						],
						sortable: false,
					},
					{
						id: 'occurrence-executed-date',
						headers: [
							{
								id: 'occurrence-executed-date-header',
								text: 'Executed',
							},
						],
						fields: [
							{
								id: 'occurrence-executed-date-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'executedDate',
										objectTable: 'payment_schedule_occurrence',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'date',
							},
						],
						sortable: true,
					},
					{
						// PD-15867 — Release / Cancel for cycle occurrences. Both
						// actions share one visibility callback: the row must be a
						// real (non-projected) cycle occurrence in `planned` or
						// `retrying`, and the viewer must hold
						// transactions:payment_schedules:update. Surfaces that do
						// not register the callbacks render no actions at all
						// (ActionsDropdownDisplay drops actions with no callback).
						id: 'occurrence-actions',
						headers: [
							{
								id: 'occurrence-actions-header',
								text: '',
							},
						],
						fields: [
							{
								id: 'occurrence-actions-dropdown',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'actions_dropdown',
								dropdownActions: [
									{
										id: 'occurrence-release-action',
										label: 'Release',
										iconName: 'circle-play',
										callbackName: 'releaseCycleOccurrence',
										variant: 'primary',
										visibility: 'visible',
										visibilityCallback: 'canActOnCycleOccurrence',
									},
									{
										id: 'occurrence-cancel-action',
										label: 'Cancel',
										iconName: 'ban',
										callbackName: 'cancelCycleOccurrence',
										variant: 'danger',
										visibility: 'overflow',
										visibilityCallback: 'canActOnCycleOccurrence',
									},
								],
							},
						],
						width: 100,
						align: 'center',
						sortable: false,
					},
				],
				defaultSortId: 'occurrence-scheduled-date-desc',
				sortOrder: 0,
			},
		],
		// The tableSort id must match the list's `defaultSortId` exactly —
		// the sort resolver looks defaultSortId up here (none of the columns
		// carry sortFields, so there is no column-sort to fall back to).
		tableSorts: [
			{
				id: 'occurrence-scheduled-date-desc',
				name: 'Scheduled Date (Newest First)',
				fields: [
					{
						objectTable: 'payment_schedule_occurrence',
						fieldName: 'scheduledDate',
						direction: 'DESC',
					},
				],
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [10, 25, 50],
		},
		filters: {
			activeFilters: [],
			defaultFilters: {},
		},
		objects: [
			{
				objectLabel: 'Payment Schedule Occurrence',
				objectTable: 'payment_schedule_occurrence',
				selectedFields: [
					{ fieldName: 'id', fieldLabel: 'ID' },
					{ fieldName: 'scheduleName', fieldLabel: 'Schedule' },
					{ fieldName: 'scheduledDate', fieldLabel: 'Scheduled Date' },
					{ fieldName: 'amount', fieldLabel: 'Amount' },
					{ fieldName: 'statusDisplay', fieldLabel: 'Status' },
					{ fieldName: 'invoiceExternalId', fieldLabel: 'Invoice' },
					{ fieldName: 'executedDate', fieldLabel: 'Executed' },
					{ fieldName: 'clientName', fieldLabel: 'Client' },
					{ fieldName: 'requiresRelease', fieldLabel: 'Requires Release' },
					{ fieldName: 'skipReason', fieldLabel: 'Skip Reason' },
				],
			},
		],
	};
